import { ReactNode, useEffect, useState } from "react"
import _ from "lodash"
import Select, { MenuPosition } from "react-select"
import CreatableSelect from "react-select/creatable"
import { IBaseFieldProperties } from "./interfaces"
import Tooltip from "../tooltip"
import { CommonErrorText } from "../../../common/errorText"

import { useAppSelector } from "../../../app/hooks"

export enum MenuPlacement {
  auto = "auto",
  top = "top",
  bottom = "bottom"
}

export interface ValueLabel {
  value: string
  label: string
}

export interface IDropdownFieldProperties extends IBaseFieldProperties {
  isMulti?: boolean
  isCreatable?: boolean
  clearable?: boolean
  closeOnSelect?: boolean
  options?: Array<ValueLabel>
  value?: string | string[]
  styles?: Object
  menuPosition?: MenuPosition
  menuPortalTarget?: HTMLElement
  autoFocus?: boolean
  labelOnReadOnly?: boolean
  filterOnValue?: boolean
  airportsLength?: number
  hasError?: boolean
  errorMsg?: string
  unique?: string[]
  isEmpty?: boolean
  tooltipRelative?: boolean
  useOwnOperatorError?: boolean
  menuPlacement?: MenuPlacement
  formatOptionLabel?: (option: ValueLabel, labelMeta: any) => ReactNode
}

const DropdownField = (props: IDropdownFieldProperties) => {
  const [creatableOptions, setCreatableOptions] = useState(
    props.isCreatable ? props.options : []
  )
  const { useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const [error, setError] = useState(false)

  useEffect(() => {
    const hasError = props.touched && props.isEmpty

    if (hasError) {
      setError(true)
    }
  }, [error, props.isEmpty, props.touched])

  useEffect(() => {
    if (props.isCreatable) {
      if (props.value) {
        const _val = props.value as string
        const updOptions = _.unionBy(
          props.options,
          [{ value: _val, label: _val }],
          (opt) => opt.value
        )
        setCreatableOptions(updOptions)
      } else {
        setCreatableOptions(props.options)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.options, props.isCreatable, props.value])

  const handleValueChange = (val) => {
    const _val = props.isMulti
      ? _.map(val, (v) => {
          return v.value
        })
      : val
      ? val.value
      : null
    let uniqueValid =
      _val && props.unique && props.unique.length > 0
        ? !_.includes(props.unique, _val)
        : true

    const isValid = props.unique ? uniqueValid && val !== "" : val !== ""
    setError(!isValid)

    props.onChange(props.name, _val, !isValid)
  }

  const handleCreatableValueChange = (val) => {
    handleValueChange(val)
    if (val && val.__isNew__ && !creatableOptions.includes(val)) {
      let updOptions = _.cloneDeep(creatableOptions)
      updOptions = [...updOptions, val]
      setCreatableOptions(updOptions)
    }
  }

  const handleBlur = () => {
    if (props.required && props.isEmpty) {
      setError(true)
    }
  }

  const getSelectedOption = (value) => {
    const opts = props.isCreatable ? creatableOptions : props.options
    if (_.isArray(value) && props.isMulti) {
      return _.filter(opts, (o) => {
        return (
          _.find(value, (v) => {
            return v === o.value
          }) != null
        )
      })
    } else {
      return (
        _.find(opts, (o) => {
          return o.value === value
        }) ?? null
      )
    }
  }

  const handleGetReadonlyValue = (value) => {
    const selectedOptions = getSelectedOption(value)
    let readonlyValue = value
    if (selectedOptions) {
      if (_.isArray(selectedOptions)) {
        readonlyValue = selectedOptions
          .map((so) => (props.labelOnReadOnly ? so.label : so.value))
          .join(", ")
      } else {
        readonlyValue = props.labelOnReadOnly
          ? selectedOptions.label
          : selectedOptions.value
      }
    }

    return readonlyValue
  }

  const showTooltip = props.unique
    ? props.unique && error
    : error || props.hasError || props.useOwnOperatorError

  const selectProps = {
    classNamePrefix: "ocs",
    className: `${
      showTooltip ? "invalid-ocs-field" : ""
    } ocs-field ocs-field-auto-height`,
    options: !props.isCreatable ? props.options : creatableOptions,
    value: getSelectedOption(props.value),
    isClearable: props.clearable,
    onChange: !props.isCreatable
      ? handleValueChange
      : handleCreatableValueChange,
    isMulti: props.isMulti,
    closeMenuOnSelect: props.closeOnSelect,
    autoFocus: props.autoFocus,
    styles: props.styles,
    menuPortalTarget: props.menuPortalTarget,
    menuPosition: props.menuPosition,
    menuPlacement: props.menuPlacement ?? MenuPlacement.auto,
    formatOptionLabel: props.formatOptionLabel,
    placeholder: props.placeholder ?? "",
    onBlur: handleBlur,
    tabSelectsValue: true,
    filterOption: (opt, rawInput) => {
      const optVal = props.filterOnValue
        ? opt.value.toLowerCase()
        : opt.label.toLowerCase()
      const inputVal = rawInput.toLowerCase()
      return optVal.includes(inputVal)
    }
  }

  return (
    <div
      className={
        " ocs-field-container " + (props.labelOnBorder ? "label-on-border" : "")
      }
    >
      <div className="d-flex">
        {props.caption && (
          <label htmlFor={props.caption}>{props.caption}</label>
        )}
        {showTooltip ? (
          <Tooltip tooltipRelative={props.tooltipRelative}>
            {props?.errorMsg ? props?.errorMsg : CommonErrorText.noEmptyValue}
          </Tooltip>
        ) : null}
      </div>
      {props.readonly ? (
        <p
          className={
            "ocs-field-readonly" +
            (props.hasChanges
              ? useLocalTime
                ? " has-changes local-time"
                : " has-changes"
              : "")
          }
        >
          {handleGetReadonlyValue(props.value)}
        </p>
      ) : !props.isCreatable ? (
        <Select {...selectProps} />
      ) : (
        <CreatableSelect
          {...selectProps}
          formatCreateLabel={(inputVal: string) => `${inputVal} *`}
          createOptionPosition="first"
        />
      )}
    </div>
  )
}

export default DropdownField

DropdownField.defaultProps = {
  styles: {},
  autoFocus: false,
  filterOnLabel: false,
  hasChanges: false
}
